import * as JsSIP from 'jssip';
import type { RTCSession } from 'jssip/lib/RTCSession';
import { createContext, ReactNode, useContext, useEffect, useSyncExternalStore } from 'react';
import { get, post } from '../lib/api';
import { CLIENT_ID } from '../lib/client-id';
import { useSession } from './session';

/**
 * Telefone no navegador (WebRTC, entrega 2.2): registra o ramal do operador no Asterisk (SIP sobre
 * WebSocket, mídia DTLS-SRTP) e controla a ligação. Só uma aba registra o ramal (Web Locks); as
 * outras ficam em espera e assumem se ela fechar. A senha SIP vem da API e fica só na memória.
 * O que aparece na tela sobre a ligação (cliente, atendimentos) vem do AMI (CallPanel).
 */

type SoftphoneConfig =
  | { enabled: false; reason: 'not_configured' | 'no_credentials' }
  | { enabled: true; wsUrl: string; domain: string; extension: string; password: string; iceServers: Array<{ urls: string }> };

export type SoftphoneStatus = 'off' | 'not_configured' | 'no_credentials' | 'connecting' | 'registered' | 'standby' | 'elsewhere' | 'error';

export interface SoftphoneCall {
  id: string;
  direction: 'incoming' | 'outgoing';
  /** Número ou ramal do outro lado. */
  remote: string;
  remoteName: string | null;
  phase: 'ringing' | 'calling' | 'active';
  muted: boolean;
  held: boolean;
  startedAt: number;
  answeredAt: number | null;
}

export interface SoftphoneState {
  status: SoftphoneStatus;
  extension: string | null;
  error: string | null;
  call: SoftphoneCall | null;
}

export interface AudioDevices {
  micId: string;
  speakerId: string;
}

const DEVICES_KEY = 'atd.softphone.devices';

function loadDevices(): AudioDevices {
  try {
    return { micId: '', speakerId: '', ...JSON.parse(localStorage.getItem(DEVICES_KEY) ?? '{}') };
  } catch {
    return { micId: '', speakerId: '' };
  }
}

/** Toque padrão brasileiro (425 Hz, 1 s tocando e 4 s em silêncio) em WAV, gerado uma vez. */
let ringtoneUrl: string | null = null;
function ringtone(): string {
  if (ringtoneUrl) return ringtoneUrl;
  const rate = 8000;
  const total = rate * 5;
  const data = new Int16Array(total);
  for (let i = 0; i < rate; i++) {
    const fade = Math.min(1, i / 200, (rate - i) / 200);
    data[i] = Math.round(Math.sin((2 * Math.PI * 425 * i) / rate) * 9000 * fade);
  }
  const buffer = new ArrayBuffer(44 + data.byteLength);
  const v = new DataView(buffer);
  const str = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  str(0, 'RIFF');
  v.setUint32(4, 36 + data.byteLength, true);
  str(8, 'WAVEfmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  str(36, 'data');
  v.setUint32(40, data.byteLength, true);
  new Int16Array(buffer, 44).set(data);
  let bin = '';
  new Uint8Array(buffer).forEach((b) => (bin += String.fromCharCode(b)));
  ringtoneUrl = `data:audio/wav;base64,${btoa(bin)}`;
  return ringtoneUrl;
}

type SinkAudio = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };

class SoftphoneEngine {
  private state: SoftphoneState = { status: 'off', extension: null, error: null, call: null };
  private readonly listeners = new Set<() => void>();
  private ua: JsSIP.UA | null = null;
  private session: RTCSession | null = null;
  private config: Extract<SoftphoneConfig, { enabled: true }> | null = null;
  private lockAbort: AbortController | null = null;
  private releaseLock: (() => void) | null = null;
  private devices: AudioDevices = loadDevices();
  private readonly remoteAudio: SinkAudio;
  private readonly ringer: SinkAudio;
  private userId: string | null = null;
  private autoAnswerUntil = 0;

  constructor() {
    this.remoteAudio = new Audio() as SinkAudio;
    this.remoteAudio.autoplay = true;
    this.ringer = new Audio() as SinkAudio;
    this.ringer.loop = true;
    JsSIP.debug.disable();
  }

  // ------------------------------------------------------------- estado
  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  snapshot = () => this.state;
  private set(patch: Partial<SoftphoneState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }
  private setCall(patch: Partial<SoftphoneCall> | null): void {
    this.set({ call: patch === null ? null : this.state.call ? { ...this.state.call, ...patch } : null });
  }

  getDevices(): AudioDevices {
    return { ...this.devices };
  }

  setDevices(d: AudioDevices): void {
    this.devices = d;
    try {
      localStorage.setItem(DEVICES_KEY, JSON.stringify(d));
    } catch {
      // ignora (navegador sem armazenamento)
    }
    void this.applySink();
  }

  private async applySink(): Promise<void> {
    for (const el of [this.remoteAudio, this.ringer]) {
      if (el.setSinkId) await el.setSinkId(this.devices.speakerId || '').catch(() => undefined);
    }
  }

  /** Toca o toque por 2 s no alto-falante escolhido (teste de som). */
  async testSpeaker(): Promise<void> {
    await this.applySink();
    this.ringer.src = ringtone();
    this.ringer.currentTime = 0;
    await this.ringer.play().catch(() => undefined);
    setTimeout(() => this.ringer.pause(), 1500);
  }

  // ------------------------------------------------------------- ciclo de vida
  async start(userId: string): Promise<void> {
    if (this.userId === userId && this.state.status !== 'off') return;
    this.stop();
    this.userId = userId;
    let cfg: SoftphoneConfig;
    try {
      cfg = await get<SoftphoneConfig>('/telephony/softphone');
    } catch {
      this.set({ status: 'error', error: 'Não foi possível carregar a configuração do telefone' });
      return;
    }
    if (this.userId !== userId) return;
    if (!cfg.enabled) {
      this.set({ status: cfg.reason, extension: null, error: null });
      return;
    }
    this.config = cfg;
    this.set({ extension: cfg.extension, error: null });
    this.requestLock(false);
  }

  /**
   * Só uma aba do navegador registra o ramal (Web Locks): as outras esperam a vez e assumem se a dona
   * fechar. `steal` toma a vez da aba dona ("Usar o telefone aqui").
   */
  private requestLock(steal: boolean): void {
    if (!this.userId) return;
    if (!navigator.locks) {
      void this.acquire(steal);
      return;
    }
    this.lockAbort?.abort();
    this.releaseLock?.();
    this.releaseLock = null;
    const ticket = new AbortController();
    this.lockAbort = ticket;
    if (!steal) this.set({ status: 'standby' });
    navigator.locks
      .request(`atd-softphone-${this.userId}`, steal ? { steal: true } : { signal: ticket.signal }, () => {
        if (this.lockAbort !== ticket) return undefined;
        void this.acquire(steal);
        return new Promise<void>((resolve) => (this.releaseLock = resolve));
      })
      .catch(() => undefined);
  }

  /**
   * Pede o ramal ao servidor, que sabe se outro navegador ou computador está com ele. `force` toma
   * o ramal: o outro recebe o aviso `softphone.claimed` e solta.
   */
  private async acquire(force: boolean): Promise<void> {
    const userId = this.userId;
    let granted = true;
    try {
      granted = (await post<{ granted: boolean }>('/telephony/softphone/claim', { clientId: CLIENT_ID, force })).granted;
    } catch {
      // Servidor sem resposta: registra assim mesmo (o telefone não pode depender disso).
    }
    if (this.userId !== userId || !this.config) return;
    if (granted) this.register();
    else this.set({ status: 'elsewhere', error: null });
  }

  /** "Usar o telefone aqui": tira o ramal da outra aba, navegador ou computador. */
  takeOver(): void {
    if (!this.config) return;
    if (this.state.status === 'standby') this.requestLock(true);
    else if (this.state.status === 'elsewhere') void this.acquire(true);
  }

  /** Outra aba tomou o ramal: sai do ramal e volta a esperar a vez. */
  onClaimed(clientId: string): void {
    if (clientId === CLIENT_ID || !this.ua) return;
    this.dropUa();
    // Mesmo navegador: espera a aba nova fechar. Outro computador: o servidor responde "em outro navegador".
    this.requestLock(false);
  }

  /** Socket reconectado (ou servidor reiniciado): confirma que esta aba continua com o ramal. */
  async reclaim(): Promise<void> {
    if (!this.ua) return;
    try {
      const r = await post<{ granted: boolean }>('/telephony/softphone/claim', { clientId: CLIENT_ID, force: false });
      if (!r.granted && this.ua) {
        this.dropUa();
        this.set({ status: 'elsewhere', error: null });
      }
    } catch {
      // ignora: tenta de novo na próxima reconexão
    }
  }

  private dropUa(): void {
    this.session?.terminate();
    this.session = null;
    this.stopRinging();
    this.remoteAudio.srcObject = null;
    if (this.ua) {
      this.ua.stop();
      this.ua = null;
    }
    this.set({ call: null });
  }

  stop(): void {
    this.lockAbort?.abort();
    this.lockAbort = null;
    this.releaseLock?.();
    this.releaseLock = null;
    if (this.ua) void post('/telephony/softphone/release', { clientId: CLIENT_ID }).catch(() => undefined);
    this.dropUa();
    this.config = null;
    this.userId = null;
    this.set({ status: 'off', extension: null, error: null, call: null });
  }

  private register(): void {
    const cfg = this.config;
    if (!cfg) return;
    if (this.ua) this.dropUa();
    this.set({ status: 'connecting', error: null });
    const socket = new JsSIP.WebSocketInterface(cfg.wsUrl);
    const ua = new JsSIP.UA({
      sockets: [socket],
      uri: `sip:${cfg.extension}@${cfg.domain}`,
      password: cfg.password,
      register: true,
      register_expires: 120,
      session_timers: false,
      user_agent: 'AIO Atendimento',
    });
    this.ua = ua;
    ua.on('connecting', () => this.ua === ua && this.state.status !== 'registered' && this.set({ status: 'connecting' }));
    ua.on('disconnected', () => this.ua === ua && this.set({ status: 'connecting', error: 'Sem conexão com o PBX; tentando de novo' }));
    ua.on('registered', () => this.ua === ua && this.set({ status: 'registered', error: null }));
    ua.on('unregistered', () => this.ua === ua && this.state.status === 'registered' && this.set({ status: 'connecting' }));
    ua.on('registrationFailed', (e) => this.ua === ua && this.set({ status: 'error', error: `Ramal recusado pelo PBX (${e.cause ?? 'erro'})` }));
    ua.on('newRTCSession', (e: { session: unknown; originator: string }) => this.onSession(e.session as RTCSession, e.originator === 'remote'));
    ua.start();
  }

  // ------------------------------------------------------------- ligações
  private onSession(session: RTCSession, incoming: boolean): void {
    if (this.session && this.session !== session) {
      // Já em ligação: recusa a segunda como ocupado (a fila manda para outro operador).
      if (incoming) session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
      return;
    }
    this.session = session;
    const remote = session.remote_identity;
    this.set({
      call: {
        id: session.id,
        direction: incoming ? 'incoming' : 'outgoing',
        remote: remote?.uri?.user ?? '?',
        remoteName: remote?.display_name || null,
        phase: incoming ? 'ringing' : 'calling',
        muted: false,
        held: false,
        startedAt: Date.now(),
        answeredAt: null,
      },
    });
    if (incoming && this.autoAnswerUntil > Date.now()) {
      this.autoAnswerUntil = 0;
      // Atendimento pedido pelo operador (botão "Atender" da fila): sem toque, atende direto.
      setTimeout(() => this.answer(), 300);
    } else if (incoming) void this.startRinging();

    // Envia a oferta logo após os primeiros candidatos (não espera a coleta inteira de ICE).
    let iceTimer: ReturnType<typeof setTimeout> | undefined;
    session.on('icecandidate', (e) => {
      clearTimeout(iceTimer);
      iceTimer = setTimeout(() => e.ready(), 1000);
    });
    const playRemote = (stream: MediaStream) => {
      if (this.session !== session) return;
      this.remoteAudio.srcObject = stream;
      void this.applySink().then(() => this.remoteAudio.play().catch(() => undefined));
    };
    const watch = (pc: RTCPeerConnection) =>
      pc.addEventListener('track', (ev: RTCTrackEvent) => playRemote(ev.streams[0] ?? new MediaStream([ev.track])));
    // Na ligação feita, o JsSIP cria a conexão ANTES de avisar a sessão nova: o evento 'peerconnection'
    // já passou e o som do cliente nunca tocava. Por isso usa a conexão que já existe.
    const existing = (session as unknown as { connection?: RTCPeerConnection }).connection;
    if (existing) watch(existing);
    else session.on('peerconnection', (e) => watch(e.peerconnection));
    /** Garantia: liga ao alto-falante as faixas de áudio que já chegaram (ex.: áudio antecipado do 183). */
    const attachReceivers = () => {
      const pc = (session as unknown as { connection?: RTCPeerConnection }).connection;
      const tracks = pc?.getReceivers().map((r) => r.track).filter((t) => t && t.kind === 'audio') ?? [];
      const current = this.remoteAudio.srcObject as MediaStream | null;
      if (tracks.length && !tracks.some((t) => current?.getTracks().includes(t))) playRemote(new MediaStream(tracks));
    };
    session.on('progress', attachReceivers);
    session.on('accepted', () => {
      this.stopRinging();
      this.setCall({ phase: 'active', answeredAt: Date.now() });
      attachReceivers();
    });
    session.on('confirmed', attachReceivers);
    session.on('hold', () => this.setCall({ held: true }));
    session.on('unhold', () => this.setCall({ held: false }));
    session.on('muted', () => this.setCall({ muted: true }));
    session.on('unmuted', () => this.setCall({ muted: false }));
    const end = () => {
      clearTimeout(iceTimer);
      if (this.session !== session) return;
      this.session = null;
      this.stopRinging();
      this.remoteAudio.srcObject = null;
      this.setCall(null);
    };
    session.on('ended', end);
    session.on('failed', end);
  }

  private mediaOptions() {
    const mic = this.devices.micId;
    return {
      mediaConstraints: { audio: mic ? { deviceId: { exact: mic } } : true, video: false },
      pcConfig: { iceServers: this.config?.iceServers ?? [], rtcpMuxPolicy: 'require' as const },
    };
  }

  private async startRinging(): Promise<void> {
    await this.applySink();
    this.ringer.src = ringtone();
    this.ringer.currentTime = 0;
    // O navegador só toca som depois de alguma interação com a página.
    await this.ringer.play().catch(() => undefined);
  }

  private stopRinging(): void {
    this.ringer.pause();
  }

  answer(): void {
    if (!this.session || this.state.call?.phase !== 'ringing') return;
    this.stopRinging();
    try {
      this.session.answer(this.mediaOptions());
    } catch (err) {
      this.set({ error: `Não foi possível atender: ${(err as Error).message}` });
    }
  }

  /** Recusa (tocando) ou desliga (em ligação). */
  hangup(): void {
    if (!this.session) return;
    const ringingIn = this.state.call?.direction === 'incoming' && this.state.call.phase === 'ringing';
    this.session.terminate(ringingIn ? { status_code: 486, reason_phrase: 'Busy Here' } : undefined);
  }

  toggleMute(): void {
    if (!this.session || this.state.call?.phase !== 'active') return;
    if (this.state.call.muted) this.session.unmute({ audio: true });
    else this.session.mute({ audio: true });
  }

  toggleHold(): void {
    if (!this.session || this.state.call?.phase !== 'active') return;
    if (this.state.call.held) this.session.unhold();
    else this.session.hold();
  }

  dtmf(tone: string): void {
    if (this.session && this.state.call?.phase === 'active') this.session.sendDTMF(tone);
  }

  /** Transferência direta (blind) para um ramal, fila ou número. */
  transfer(target: string): void {
    const session = this.session;
    if (!session || !this.config || this.state.call?.phase !== 'active') return;
    // Transferência direta: aceita pelo PBX, esta perna sai da ligação (o cliente segue para o destino).
    let done = false;
    const leave = () => {
      if (done) return;
      done = true;
      if (this.session === session && !session.isEnded()) session.terminate();
    };
    session.refer(`sip:${target.trim()}@${this.config.domain}`, {
      eventHandlers: {
        requestSucceeded: () => setTimeout(leave, 3000),
        accepted: leave,
        requestFailed: () => this.set({ error: `Transferência para ${target} recusada pelo PBX` }),
        failed: () => this.set({ error: `Transferência para ${target} não completou` }),
      },
    });
  }

  /** O próximo toque (até `ms`) é atendido sozinho: o operador pediu para atender da fila. */
  autoAnswerNext(ms = 20_000): void {
    this.autoAnswerUntil = Date.now() + ms;
  }

  /** Ligar para um ramal ou número. */
  call(target: string): void {
    const t = target.replace(/[^\d*#+]/g, '');
    if (!this.ua || !this.config || this.state.status !== 'registered' || this.session || !t) return;
    try {
      this.ua.call(`sip:${t}@${this.config.domain}`, this.mediaOptions());
    } catch (err) {
      this.set({ error: `Não foi possível ligar: ${(err as Error).message}` });
    }
  }
}

const engine = new SoftphoneEngine();
const SoftphoneContext = createContext(engine);

/** Liga o telefone quando o usuário entra e desliga quando sai. */
export function SoftphoneProvider({ children }: { children: ReactNode }) {
  const { user, socket } = useSession();
  useEffect(() => {
    if (user?.id) void engine.start(user.id);
    else engine.stop();
  }, [user?.id]);
  useEffect(() => {
    if (!socket) return;
    const onClaimed = (d: { clientId?: unknown } | null) => typeof d?.clientId === 'string' && engine.onClaimed(d.clientId);
    const onConnect = () => void engine.reclaim();
    socket.on('softphone.claimed', onClaimed);
    socket.on('connect', onConnect);
    return () => {
      socket.off('softphone.claimed', onClaimed);
      socket.off('connect', onConnect);
    };
  }, [socket]);
  useEffect(() => {
    const onUnload = () => engine.stop();
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);
  return <SoftphoneContext.Provider value={engine}>{children}</SoftphoneContext.Provider>;
}

export function useSoftphone() {
  const e = useContext(SoftphoneContext);
  const state = useSyncExternalStore(e.subscribe, e.snapshot);
  return { ...state, engine: e };
}
